'use client'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import {
  Bell,
  Menu,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { SidebarTrigger } from '@/components/ui/sidebar'
import AppHeaderActions from '@/components/header/AppHeaderActions'
import { ServiceWorkerRegister } from '@/components/pwa/ServiceWorkerRegister'
import { PWAInstallBanner } from '@/components/pwa/PWAInstallBanner'
import { PushPermissionPrompt } from '@/components/pwa/PushPermissionPrompt'
import { WorkspaceAlertCenter } from '@/components/alerts/WorkspaceAlertCenter'
import { BusinessMembershipPaymentModal } from '@/components/alerts/BusinessMembershipPaymentModal'
import { OnboardingProgressBanner } from '@/components/dashboard/onboarding/OnboardingProgressBanner'

import { useChannel } from '@/contexts/ChannelContext'
import { useFunnelBuilderMode } from '@/contexts/FunnelBuilderModeContext'

import { runCommunityDemo } from '@/features/community/demo/communityDemoEngine'
import { cn } from '@/lib/utils'
import {
  DemoResetButton,
} from '@/components/demo/DemoResetButton'

const ConfigSidebar = dynamic(
  () =>
    import('@/components/ConfigSidebar').then(
      (module) => module.ConfigSidebar
    ),
  { ssr: false }
)

const ContextNav = dynamic(
  () =>
    import('@/components/MainNav').then(
      (module) => module.ContextNav
    ),
  { ssr: false }
)

const RoomsChat = dynamic(
  () =>
    import('@/components/rooms/RoomsChat').then(
      (module) => module.RoomsChat
    ),
  { ssr: false }
)

type AppShellSlots = {
  background?: React.ReactNode
  sidebar?: React.ReactNode
  header?: React.ReactNode
  mobileHeader?: React.ReactNode
}

type InternalAppShellProps = {
  children: React.ReactNode

  gate: {
    sellerId: number
    isOwner: boolean
    isAdmin: boolean
    accessStatus: string
    approvalRequired: boolean
    tag?: string
    access?: any
  }

  enableGlassUI: boolean
  brand?: any
  backgroundImage?: string | null

  loggedIn: boolean
  userId: any
  features: any

  isAffiliateActive: boolean
  inviteUrl: string | null

  isCRMRoute: boolean
  isPromanityDashboard?: boolean

  sellerId?: number | null

  slots?: AppShellSlots

  immersive?: boolean
}

export function InternalAppShell({
  children,
  gate,
  enableGlassUI,
  backgroundImage,
  loggedIn,
  userId,
  features,
  isAffiliateActive,
  inviteUrl,
  sellerId,
  slots,
  immersive,
}: InternalAppShellProps) {
  const pathname = usePathname() || ''
  const [isSharedHubee, setIsSharedHubee] = useState(false)
  useEffect(() => {
    setIsSharedHubee(window.location.hostname.replace(/^www\./, '').toLowerCase() === 'hubee.app')
  }, [])

  const isLaunchpad =
    pathname === '/launchpad'

  const isCommunity =
    pathname === '/community'

  const isOnboarding =
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/')

  const isFunnelRoute =
    pathname === '/funnel-builder' ||
    pathname.startsWith('/funnel-builder/')

  const isMomentsRoute = pathname === '/moments' || pathname.startsWith('/moments/')

  const [isMomentsWelcome, setIsMomentsWelcome] =
    useState(false)

  const {
    selectedChannel,
    selectedChannelName,
  } = useChannel()

  const {
    builderMode,
  } = useFunnelBuilderMode()

  const isImmersive =
    immersive === true

  /*
   * Samantha
   * --------------------------------------------------
   * Conservamos exactamente el comportamiento del
   * HubeeShell nuevo.
   */
  const [
    samanthaDockOpen,
    setSamanthaDockOpen,
  ] = useState(false)

  const samanthaHiddenPrefixes = [
    '/f',
    '/funnel-view',
    '/funnel-preview',
    '/inbox',
    '/login',
    '/product',
    '/start',
    '/activate',
    '/awaken',
    '/settings',
  ]

  const isSamanthaHidden =
    !pathname ||
    pathname === '/' ||
    samanthaHiddenPrefixes.some(
      (prefix) =>
        pathname === prefix ||
        pathname.startsWith(`${prefix}/`)
    )

  const hasSamanthaWidget =
    !isSamanthaHidden

  useEffect(() => {
    const handleMomentsWelcome = (
      event: Event
    ) => {
      const customEvent =
        event as CustomEvent<{
          hidden?: boolean
        }>

      setIsMomentsWelcome(
        isMomentsRoute &&
          Boolean(
            customEvent.detail?.hidden
          )
      )
    }

    window.addEventListener(
      'samantha:visibility',
      handleMomentsWelcome
    )

    return () => {
      window.removeEventListener(
        'samantha:visibility',
        handleMomentsWelcome
      )
    }
  }, [isMomentsRoute])

  useEffect(() => {
    function handleSamanthaDockVisibility(
      event: Event
    ) {
      const customEvent =
        event as CustomEvent<{
          open?: boolean
        }>

      setSamanthaDockOpen(
        Boolean(customEvent.detail?.open)
      )
    }

    window.addEventListener(
      'samantha:dock-visibility-change',
      handleSamanthaDockVisibility
    )

    return () => {
      window.removeEventListener(
        'samantha:dock-visibility-change',
        handleSamanthaDockVisibility
      )
    }
  }, [])

  /*
   * Community / Rooms
   * --------------------------------------------------
   */
  const activeCategoryId = useMemo(() => {
    if (
      !selectedChannel ||
      selectedChannel === 'general'
    ) {
      return 0
    }

    const categoryId =
      Number(selectedChannel)

    return Number.isFinite(categoryId)
      ? categoryId
      : 0
  }, [selectedChannel])

  const activeChannelName = useMemo(() => {
    if (
      !selectedChannel ||
      selectedChannel === 'general'
    ) {
      return 'Todos'
    }

    return (
      selectedChannelName ||
      'Canal'
    )
  }, [
    selectedChannel,
    selectedChannelName,
  ])

  const shouldRenderRooms =
    isCommunity &&
    Boolean(sellerId) &&
    activeCategoryId > 0

  /*
   * Onboarding
   * --------------------------------------------------
   */
  const shouldShowFloatingOnboarding =
    gate.isOwner &&
    Boolean(sellerId) &&
    !isFunnelRoute

  /*
   * Immersive
   * --------------------------------------------------
   */
  if (isImmersive) {
    return (
      <div
        className={cn(
          'relative h-dvh w-dvw overflow-hidden bg-black text-foreground',
          enableGlassUI && 'glass-mode'
        )}
      >
        {slots?.background}

        <main className="relative z-10 h-full w-full overflow-hidden">
          {children}
        </main>
      </div>
    )
  }

  return (
      <div
        className={cn(
          'relative flex h-screen w-full overflow-hidden bg-background text-foreground',
          enableGlassUI && 'glass-mode'
        )}
        style={
          backgroundImage && !isOnboarding
            ? {
                backgroundImage: `linear-gradient(180deg, hsl(var(--background) / 0.32), hsl(var(--background) / 0.66)), url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : undefined
        }
      >
        <ServiceWorkerRegister />

        {/*
        * Optional background slot
        */}
        {slots?.background}

        {isOnboarding && backgroundImage ? (
          <>
            {/* DESKTOP WALLPAPER */}
            <div
              className="pointer-events-none absolute inset-0 z-0 hidden md:block"
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />

            {/* DESKTOP LEFT FADE */}
            <div
              className="
                pointer-events-none
                absolute inset-y-0 left-0 z-[1]
                hidden w-full
                bg-gradient-to-r
                from-background
                via-background/95
                to-transparent
                md:block
              "
            />

            {/* MOBILE WALLPAPER HERO */}
            <div
              className="
                pointer-events-none
                absolute inset-x-0 top-0 z-0
                h-[78vh]
                md:hidden
              "
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
              }}
            />

            {/* MOBILE WALLPAPER FADE */}
            <div
              className="
                pointer-events-none
                absolute inset-x-0 top-0 z-[1]
                h-[90vh]
                bg-gradient-to-b
                from-transparent
                via-transparent
                to-background
                md:hidden
              "
            />

            {/* MOBILE TOP PRIMARY LIGHT */}
            <div
              className="
                pointer-events-none
                absolute -left-16 top-10 z-[2]
                h-[260px] w-[320px]
                rounded-full
                bg-primary/16
                blur-[90px]
                md:hidden
              "
            />
          </>
        ) : null}

        {/*
        * Ambient background
        */}
        <div
          className={cn(
            'pointer-events-none absolute inset-0 hidden overflow-hidden md:block',
            isOnboarding && 'md:hidden'
          )}
        >
          <div className="absolute -left-24 top-24 h-[360px] w-[360px] rounded-full bg-primary/10 blur-3xl" />

          <div className="absolute -right-20 top-32 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />

          <div className="absolute bottom-[-160px] left-[18%] h-[360px] w-[520px] rounded-full bg-primary/5 blur-3xl" />
        </div>

        {/* Imagen ambiental del Space activo */}
        <div
          id="hubee-space-ambient-root"
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
        />

        {/*
        * Global sidebar
        */}
        <ConfigSidebar
          enableGlassUI={enableGlassUI}
        />

        {/* HUBEE PLATFORM LOGO — DESKTOP ONLY */}
        {isSharedHubee && loggedIn && (
          <a
            href="/dashboard"
            aria-label="Ir a Hubee"
            className="
              pointer-events-auto
              fixed left-6 top-4 z-[50]
              hidden h-12 items-center
              md:flex
              transition-opacity hover:opacity-80
            "
          >
            <img
              src="/images/hubee-mark.svg"
              alt="Hubee"
              className="h-4 w-auto pl-1 dark:invert"
              style={{ imageRendering: 'auto' }}
            />
          </a>
        )}

        {/*
        * Desktop floating header
        */}
        {slots?.header ??
          (
            loggedIn ? (
              <div
                data-app-header
                data-hubee-chrome="true"
                className="pointer-events-none fixed top-4 right-6 z-[50] hidden h-12 items-center md:flex"
              >
                <button
                  className="hidden"
                  type="button"
                  onClick={() => {
                    if (
                      window.location.pathname ===
                      '/community'
                    ) {
                      runCommunityDemo()
                    }
                  }}
                >
                  ?
                </button>

                <AppHeaderActions
                  loggedIn={loggedIn}
                  userId={userId}
                  features={features}
                  isAffiliateActive={isAffiliateActive}
                  inviteUrl={inviteUrl}
                  className="pointer-events-auto"
                />
              </div>
            ) : null
          )}

        {/*
        * Main application area
        */}
        <div
          className={cn(
            'relative z-10 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:pr-6',

            /*
            * Bottom spacing.
            */
            'md:pb-0',

            /*
            * Important:
            * this is the offset that coordinated
            * the floating ConfigSidebar with content.
            */
            'md:pl-7',

            /*
            * Samantha dim / focus behavior
            */
            'transition-[opacity,filter,transform] duration-200 ease-out',

            samanthaDockOpen &&
              !isLaunchpad &&
              'pointer-events-none scale-[0.995] opacity-45 blur-[1px]',

            /*
            * Vertical chrome spacing.
            */
            isMomentsWelcome
              ? 'md:pt-0'
              : isLaunchpad
                ? 'md:pt-6'
                : hasSamanthaWidget
                  ? 'md:pt-[86px]'
                  : 'md:pt-[60px]'
          )}
        >
          {/*
          * Mobile Header
          */}
          {slots?.mobileHeader ?? (
            <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
              <SidebarTrigger>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SidebarTrigger>

              {isSharedHubee && (
                <a href="/dashboard" className="flex min-w-0 flex-1 items-center" aria-label="Hubee">
                  <img src="/images/hubee-mark.svg" alt="Hubee" className="h-4 w-auto pl-1 dark:invert" style={{ imageRendering: 'auto' }} />
                </a>
              )}
              <div className="hidden min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">
                  Hubee
                </div>
              </div>

              {loggedIn ? (
                <>
                  <AppHeaderActions
                    loggedIn={loggedIn}
                    userId={userId}
                    features={features}
                    isAffiliateActive={isAffiliateActive}
                    inviteUrl={inviteUrl}
                  />
                </>
              ) : null}
            </header>
          )}

          {/*
          * COMMUNITY
          * ==================================================
          * 1512 px overall layout:
          *
          * 300px channels
          * 780px feed
          * 400px rooms
          */}
          {isCommunity ? (
            <div className="h-full min-h-0 min-w-0 flex-1 overflow-visible">
              <div className="mx-auto h-full min-h-0 w-full max-w-[1512px]">
                <div
                  className={cn(
                    'grid h-full min-h-0 grid-cols-1 gap-5 overflow-visible p-0',
                    shouldRenderRooms
                      ? 'xl:grid-cols-[300px_minmax(0,780px)_400px]'
                      : 'xl:grid-cols-[300px_minmax(0,1fr)]'
                  )}
                >

                  {slots?.sidebar ??
                    <ContextNav />}

                  {/*
                  * Community Feed
                  */}
                  <main className="h-full min-h-0 min-w-0 overflow-visible">
                    {gate.isOwner && (
                      <BusinessMembershipPaymentModal />
                    )}
                    {children}
                  </main>

                  {/*
                  * Rooms
                  */}
                  {shouldRenderRooms ? (
                    <div
                      data-tour="community-chat-panel"
                      className="hidden min-h-0 w-full shrink-0 rounded-[2rem] hubee-premium-card xl:block"
                    >
                      <div className="hubee-premium-content h-full min-h-0 overflow-hidden">
                        <RoomsChat
                          sellerId={
                            Number(sellerId)
                          }
                          categoryId={
                            activeCategoryId
                          }
                          channelName={
                            activeChannelName
                          }
                          className="h-full border-0 bg-transparent shadow-none"
                          tourMode
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            /*
            * REST OF HUBEE
            * ==================================================
            */
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row md:gap-5">
              {slots?.sidebar ??
                <ContextNav />}

              <main
                className={cn(
                  'min-h-0 min-w-0 flex-1 overflow-y-auto',

                  hasSamanthaWidget &&
                    !isMomentsWelcome &&
                    !isLaunchpad &&
                    '',

                  isFunnelRoute &&
                    builderMode === 'editor' &&
                    'md:overflow-hidden'
                )}
              >
              <div
                className={cn(
                  hasSamanthaWidget &&
                    !isMomentsWelcome &&
                    !isLaunchpad &&
                    'md:pt-2'
                )}
              >

                {gate.isOwner && (
                  <BusinessMembershipPaymentModal />
                )}

                {children}

                <DemoResetButton />

                </div>
                
              </main>
            </div>
          )}
        </div>

        {/*
        * Floating onboarding
        */}
        {shouldShowFloatingOnboarding ? (
          <>
            <div className="pointer-events-none fixed bottom-6 left-[112px] z-[65] hidden md:block">
              <OnboardingProgressBanner
                sellerId={
                  Number(sellerId || 0)
                }
                variant="floating"
                className="pointer-events-auto w-[360px]"
              />
            </div>

            <div className="pointer-events-none fixed bottom-4 left-4 right-4 z-[65] md:hidden">
              <OnboardingProgressBanner
                sellerId={
                  Number(sellerId || 0)
                }
                variant="floating"
                className="pointer-events-auto"
              />
            </div>
          </>
        ) : null}

        {gate.isOwner && (
          <WorkspaceAlertCenter
            sellerMembershipActive={
              gate.access
                ?.sellerMembershipActive
            }
            sellerMembershipExpiry={
              gate.access
                ?.sellerMembershipExpiry
            }
            paymentUrl={
              gate.access
                ?.paymentUrl ||
              '/setup?reason=membership_expiring'
            }
            hasStripeSubscription={
              gate.access
                ?.hasStripeSubscription
            }
            stripeSubscriptionStatus={
              gate.access
                ?.stripeSubscriptionStatus
            }
            stripeCurrentPeriodEnd={
              gate.access
                ?.stripeCurrentPeriodEnd
            }
          />
        )}

        <PWAInstallBanner />

        <PushPermissionPrompt
          userId={
            loggedIn && userId
              ? Number(userId)
              : null
          }
          sellerId={
            sellerId
              ? Number(sellerId)
              : null
          }
        />
      </div>
  )
}